import React, { useRef, useLayoutEffect, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PageWrapper from './PageWrapper';
import { selectPageSlice } from '../Selector';
import { reportPageCount } from '../../../redux/actions';

// Must match index.scss variables exactly
const PAGE_HEIGHT = 1123;
const PADDING_TOP = 50;
const PADDING_BOTTOM = 60;
const FOOTER_HEIGHT = 40;
// A small safety buffer subtracted from the usable height. The page itself has
// overflow:hidden, so any sub-pixel rounding between measurement and the real
// render that pushes content even slightly over the boundary silently clips the
// last item. Reserving a few pixels keeps that from happening.
const SAFETY_BUFFER = 4;
const CONTENT_HEIGHT = PAGE_HEIGHT - PADDING_TOP - PADDING_BOTTOM - FOOTER_HEIGHT - SAFETY_BUFFER; // 969px
const CONTENT_WIDTH = 794 - 60 - 60; // 674px  (794 - 2 * horizontal padding)

/**
 * Distributes a flat list of items across one or more A4 pages.
 *
 * `header` is rendered only on the FIRST page.  Continuation pages use the
 * full content height so the page fills completely before overflowing.
 *
 * Props
 *   header          ReactElement   – rendered only on the first page
 *   items           ReactElement[] – individual content elements to distribute
 *   startPageNumber number         – page number printed on the first page
 *   personName      string         – name shown in the footer
 *   sliceKey        string?        – pageContent discriminator to re-measure on
 */
function MultiPage({ header, items, startPageNumber, personName, sliceKey, slice, sectionKey, reportPageCount }) {
  const measureRef = useRef(null);
  const [pages, setPages] = useState(null);

  const isFirstSliceRef = useRef(true);
  useEffect(() => {
    if (isFirstSliceRef.current) {
      isFirstSliceRef.current = false;
      return;
    }
    setPages(null);
  }, [slice]);

  useLayoutEffect(() => {
    if (pages) return;
    if (!measureRef.current) return;

    const container = measureRef.current;
    const headerEl = container.querySelector('[data-mp="header"]');
    const itemEls = Array.from(container.querySelectorAll('[data-mp="item"]'));

    // Full vertical footprint of a measured slot, including the vertical
    // margins of its content. getBoundingClientRect()/offsetHeight exclude
    // margins, but the rendered page DOES include them (e.g. the marginTop on
    // continuation section headers, set inline by SectionHeader). The margins
    // live on the wrapped child, not on the [data-mp] wrapper itself, so read
    // them from the child. Undercounting here makes a page measure as "fits"
    // when it actually overflows, clipping the last item against the page's
    // overflow:hidden.
    const outerHeight = (slot) => {
      if (!slot) return 0;
      let h = slot.getBoundingClientRect().height;
      const child = slot.firstElementChild;
      if (child) {
        const cs = window.getComputedStyle(child);
        h += (parseFloat(cs.marginTop) || 0) + (parseFloat(cs.marginBottom) || 0);
      }
      return h;
    };

    const headerH = outerHeight(headerEl);

    const page1Available = CONTENT_HEIGHT - headerH;
    const restAvailable = CONTENT_HEIGHT;

    const allPages = [];
    let pageItems = [];
    let usedH = 0;
    let isFirstPage = true;

    items.forEach((item, idx) => {
      const h = outerHeight(itemEls[idx]);
      const avail = isFirstPage ? page1Available : restAvailable;

      if (pageItems.length > 0 && usedH + h > avail) {
        allPages.push(pageItems);
        pageItems = [item];
        usedH = h;
        isFirstPage = false;
      } else {
        pageItems.push(item);
        usedH += h;
      }
    });

    if (pageItems.length > 0) {
      allPages.push(pageItems);
    }

    const groups = allPages.length > 0 ? allPages : [items];
    setPages(groups);

    if (sectionKey && reportPageCount) {
      reportPageCount(sectionKey, groups.length);
    }
  }, [pages, items, sectionKey, reportPageCount]);

  // ── Phase 1: measurement ───────────────────────────────────────────────────
  if (!pages) {
    return (
      <div style={{ position: 'relative', height: 0, overflow: 'hidden' }}>
        <div
          ref={measureRef}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: CONTENT_WIDTH,
            visibility: 'hidden',
            pointerEvents: 'none',
          }}
        >
          {header && <div data-mp="header">{header}</div>}
          {items.map((item, i) => (
            <div key={i} data-mp="item">
              {item}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Phase 2: render ────────────────────────────────────────────────────────
  return (
    <>
      {pages.map((group, idx) => (
        <PageWrapper
          key={idx}
          pageNumber={startPageNumber + idx}
          personName={personName}
        >
          {idx === 0 && header}
          {group}
        </PageWrapper>
      ))}
    </>
  );
}

const mapStateToProps = (state, ownProps) => ({
  slice: selectPageSlice(state, ownProps.sliceKey || null),
});

const mapDispatchToProps = (dispatch) => ({
  reportPageCount: (sectionKey, count) => dispatch(reportPageCount(sectionKey, count)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MultiPage);
